import React from 'react';
import * as Sentry from '@sentry/react';
import { toSentryError } from '@pps/observability';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface Props {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class RouteErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[RouteErrorBoundary]', error, errorInfo);
    Sentry.captureException(toSentryError(error), {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      tags: { boundary: 'route' },
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} resetError={this.resetError} />;
      }

      return <RouteErrorBoundaryContent error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

function RouteErrorBoundaryContent({ error, resetError }: { error: Error | null; resetError: () => void }) {
  const { t } = useTranslation('layout');

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md mx-auto text-center p-6 bg-card rounded-lg shadow-lg border">
        <div className="flex justify-center mb-4">
          <AlertCircle className="h-16 w-16 text-destructive" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t('errors.title')}
        </h2>
        <p className="text-muted-foreground mb-4">
          {t('errors.subtitle')}
        </p>
        <div className="space-y-3">
          <Button 
            onClick={resetError}
            className="w-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('errors.retry')}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/'}
            className="w-full"
          >
            {t('errors.back')}
          </Button>
        </div>
        {import.meta.env.DEV && error && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground">
              {t('errors.devDetails')}
            </summary>
            <pre className="mt-2 text-xs bg-muted p-3 rounded overflow-auto">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default RouteErrorBoundary;